<script setup>
import { computed, ref, watch } from 'vue'
import DataLayer from '@services/data-layer/index'
import MixLargeTile from '@components/mix-large-tile'
import MixTableBlock from '@components/mix-table-block.v2'
import MixGridBlock from '@components/mix-grid-block'
import { useAppearanceStore } from '@state/stores/appearance'

const INITIAL_POPULAR_MIXES = 5
const INITIAL_ALL_MIXES = 12
const LOAD_MORE_THROTTLE_MS = 200
const BOTTOM_OFFSET_RATIO = 3

const props = defineProps({
    artists: {
        type: Array,
        default: () => [],
    },
    show: {
        type: Object,
        default: () => {},
    },
    popularMixes: {
        type: Array,
        default: () => [],
    },
    latestMixes: {
        type: Array,
        default: () => [],
    },
})

const appearanceStore = useAppearanceStore()

// Shrinks by one when a page comes back empty, which is what stops `mixesRemain`.
const allMixesPageSize = ref(INITIAL_ALL_MIXES)
const visibleAllMixes = ref(INITIAL_ALL_MIXES)
const visiblePopularMixes = ref(INITIAL_POPULAR_MIXES)
const shouldLoadMore = ref(true)
const mixes = ref([...props.latestMixes])

const currentMix = computed(() => mixes.value[0])

const mixesRemain = computed(() => mixes.value.length % allMixesPageSize.value === 0)

const scroll = computed(() => appearanceStore.scroll)

function showMorePopularMixes() {
    visiblePopularMixes.value *= 2
}

function showMoreLatestMixes() {
    if (!mixesRemain.value) return

    DataLayer.mixes.getLatestByShow(props.show.id, allMixesPageSize.value, visibleAllMixes.value).then(response => {
        mixes.value.push(...response.body)
        if (response.body.length === 0) {
            allMixesPageSize.value -= 1
        }
        shouldLoadMore.value = true
    })
    visibleAllMixes.value += allMixesPageSize.value
}

watch(
    () => props.latestMixes,
    newMixes => {
        mixes.value = [...newMixes]
    }
)

watch(scroll, () => {
    const bottomOfWindow =
        scroll.value + window.innerHeight >=
        appearanceStore.scrollChild.offsetHeight - window.innerHeight / BOTTOM_OFFSET_RATIO

    if (bottomOfWindow && shouldLoadMore.value) {
        shouldLoadMore.value = false
        setTimeout(() => {
            shouldLoadMore.value = true
        }, LOAD_MORE_THROTTLE_MS)
        showMoreLatestMixes()
    }
})
</script>

<template>
    <BaseContent>
        <BaseSection
            padded
            title="Latest Mix"
        >
            <MixLargeTile
                v-if="currentMix"
                :mix="currentMix"
            >
                <template #button>
                    <BaseButtonPlay
                        :resource="currentMix"
                        button-type="secondary"
                    />
                </template>
            </MixLargeTile>
        </BaseSection>

        <BaseSection
            v-if="popularMixes.length"
            padded
            title="Popular Mixes"
        >
            <MixTableBlock
                :mixes="popularMixes.slice(0, visiblePopularMixes)"
                :genres="true"
                :buttons="{ playCount: true }"
                is-indexed-list
            />
            <div
                v-if="popularMixes.length > INITIAL_POPULAR_MIXES && popularMixes.length > visiblePopularMixes"
                class="load-more-button"
                @click="showMorePopularMixes"
            >
                <span>Load more</span>
                <BaseIcon
                    class="icon-arrow-down"
                    name="arrow-long-down"
                /></div></BaseSection>

        <BaseSection
            v-if="mixes.length"
            padded
            title="Mixes"
        >
            <MixGridBlock :mixes="mixes" />
            <div
                v-if="mixesRemain"
                class="load-more-button"
                @click="showMoreLatestMixes"
            >
                <span>Load more</span>
                <BaseIcon
                    class="icon-arrow-down"
                    name="arrow-long-down"
                />
            </div>
        </BaseSection>
    </BaseContent>
</template>

<style lang="scss" scoped>
@import '@design';

.load-more-button {
    margin-top: 25px;
    text-align: right;
    cursor: pointer;

    @extend %contentTextButton;
}

.icon-arrow-down {
    width: 36px;
    height: 16px;
    vertical-align: middle;
    fill: $c-highlight;
}
</style>
